// Shopify Customer Privacy API integration (verified against
// https://shopify.dev/docs/api/customer-privacy as of this build).
// Fails CLOSED: if the API never becomes available, or reports consent has
// not been granted, we never collect analytics. Global Privacy Control is
// honored automatically by Shopify's own consent banner/API — we don't
// need to read navigator.globalPrivacyControl ourselves, Shopify folds it
// into analyticsProcessingAllowed() for regions where GPC applies.

type ShopifyCustomerPrivacy = {
  analyticsProcessingAllowed: () => boolean;
};

declare global {
  interface Window {
    Shopify?: {
      customerPrivacy?: ShopifyCustomerPrivacy;
      loadFeatures?: (
        features: { name: string; version: string }[],
        callback: (error?: unknown) => void
      ) => void;
      designMode?: boolean; // true inside the theme editor
    };
  }
}

const CONSENT_POLL_TIMEOUT_MS = 3000;
const CONSENT_POLL_INTERVAL_MS = 150;

function readConsentNow(): boolean | null {
  const api = window.Shopify?.customerPrivacy;
  if (!api) return null;
  try {
    return api.analyticsProcessingAllowed();
  } catch {
    return null;
  }
}

/** Resolves once we have a definitive consent answer, or false after a short timeout (fail closed). */
export function getInitialConsent(): Promise<boolean> {
  return new Promise((resolve) => {
    const immediate = readConsentNow();
    if (immediate !== null) {
      resolve(immediate);
      return;
    }

    // API not present yet — try the explicit feature-load path some theme
    // configurations still require, then poll briefly either way.
    window.Shopify?.loadFeatures?.([{ name: "consent-tracking-api", version: "0.1" }], () => {
      /* ignore result; polling below is the real source of truth */
    });

    const start = Date.now();
    const interval = setInterval(() => {
      const value = readConsentNow();
      if (value !== null) {
        clearInterval(interval);
        resolve(value);
        return;
      }
      if (Date.now() - start > CONSENT_POLL_TIMEOUT_MS) {
        clearInterval(interval);
        resolve(false); // fail closed
      }
    }, CONSENT_POLL_INTERVAL_MS);
  });
}

/** Invokes `onChange` whenever the visitor updates their consent choice mid-session. */
export function onConsentChange(onChange: (analyticsAllowed: boolean) => void): void {
  document.addEventListener("visitorConsentCollected", (event) => {
    const detail = (event as CustomEvent<{ analyticsAllowed?: boolean }>).detail;
    if (detail && typeof detail.analyticsAllowed === "boolean") {
      onChange(detail.analyticsAllowed);
    } else {
      onChange(readConsentNow() ?? false);
    }
  });
}

export function isThemeEditor(): boolean {
  return Boolean(window.Shopify?.designMode);
}
